import { Fragment, useState, useEffect, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useAxios from "axios-hooks";
import { Container, Row, Col, Image, Button, Form, Stack, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import ReactQuill from "react-quill";
import BrandContext from "./BrandContext";
import { LinkContainer } from "react-router-bootstrap";

export default function Content(props) {
    const params = useParams();
    const nav = useNavigate();
    const theme = useContext(BrandContext);
    const [prBody, setPrBody] = useState('');
    const [prAbout, setPrAbout] = useState('');
    const [prTitle, setPrTitle] = useState('');
    const [prPostDate, setPostDate] = useState(new Date());
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const handleAcceptedTerms = (e) => {
        setAcceptedTerms(e.target.checked)
    };
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const [{ data: currentPR, loading: loadingPR, error: prError }, refetchPR] = useAxios({
        method: 'get',
        url: '/pr/get',
        params: {
            id: params.prId
        },
    });
    useEffect(() => {
        // Details weren't completed, go back to content
        if (currentPR && !currentPR?.company) {
            nav('../details');
            return;
        }
        const status = currentPR?.status;
        switch (status) {
            case 'paid':
                nav('../checkout');
                return;
            case 'submitted':
                nav('../submitted');
                return;
            default:
                break;
        }
        setPrBody(currentPR?.prBody || '')
        setPrAbout(currentPR?.prAbout || '')
        setPrTitle(currentPR?.prTitle || '')
        setPostDate(currentPR?.prPostDate ? new Date(currentPR?.prPostDate) : new Date())
        setHeaderImage(currentPR?.prHeaderImage ? currentPR?.prHeaderImage + `?x-cache-bust=${Date.now()}` : '')
    }, [currentPR]);

    const [
        { data: draft, loading: working, error: createError },
        executePut
    ] = useAxios(
        {
            url: '/pr/content',
            method: 'POST'
        },
        { manual: true }
    )

    function updateContent() {
        executePut({
            data: {
                id: params.prId,
                prTitle: prTitle,
                prBody: prBody,
                prAbout: prAbout,
                prPostDate: prPostDate || new Date().toISOString(),
                acceptedTerms,
                prHeaderImage: headerImage
            }
        })
            .then((r) => {
                nav('../pitch')
            })
            .catch((e) => {
                console.error(e)
            })
            .finally(() => {

            })
    }

    const [
        { data: uploadResult, loading: uploading, error: errorUploading },
        executeUpload
    ] = useAxios(
        {
            url: '/pr/header-image',
            method: 'POST',
        },
        { manual: true }
    )
    const filePickerRef = useRef();
    const [headerImage, setHeaderImage] = useState('');
    function readFileAsync(file) {
        return new Promise((resolve, reject) => {
            let reader = new FileReader();
            reader.onload = () => { resolve(reader.result); };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        })
    }
    async function loadImage(file) {
        setHeaderImage('')
        executeUpload({
            data: {
                id: params.prId,
                file: await readFileAsync(file)
            }
        }).then(resp => {
            setHeaderImage(resp.data.url)
        })
    }
    const onHeaderChange = () => {
        const files = filePickerRef.current.files;
        if (files && files.length > 0 && files[0]) {
            if (files[0].size > 100000000) {
                console.log(`File too large ${files[0].size}`)
            }
            else {
                loadImage(files[0]);
            }
        }
    }

    return (
        <Fragment>
            <Container fluid="md" style={{ paddingTop: "50px", paddingBottom: "50px" }}>
                <Row>
                    <Col sm={{ span: true, order: 'first' }} xs={{ span: 12, order: 'last' }} style={{ padding: "15px" }}>
                        <p style={{ fontSize: "14px" }}><span className="fw-bold">Create</span> <span className="fw-bold text-swiftrev">&gt;</span> <span className="sr-light">Promote</span> <span className="fw-bold text-swiftrev">&gt;</span> <span className="sr-light">Submit</span></p>
                        <p className="fw-bold" style={{ fontSize: "24px", lineHeight: "26px" }}>Create / edit your press release</p>
                    </Col>
                    <Col sm={{ span: 'auto', order: 'last' }} xs={{ span: 12, order: 'first' }} style={{ padding: "15px", display: "grid", alignContent: "start", justifyContent: "start" }}>
                        <Image style={{ height: "50px" }} fluid src={theme?.marketing?.logo}></Image>
                    </Col>
                </Row>
                {currentPR &&
                    <Fragment>
                        <Row style={{ marginTop: "12px" }}>
                            <Col md="8" style={{ padding: "15px" }}>
                                <h5>Press Release Title</h5>
                                <Form.Control tabIndex={1} as="textarea" rows={2} style={{ resize: "none" }}
                                    onChange={(e) => { setPrTitle(e.target.value) }} required minLength={1}
                                    defaultValue={currentPR?.prTitle || ''} />
                                <br />
                                <h5>Press Release Body</h5>
                                <ReactQuill tabIndex={2} theme="snow" value={prBody} onChange={setPrBody}>
                                    <div className="swiftrev-ql" id="a" style={{ height: "500px" }} />
                                </ReactQuill>
                                <br />
                                <h5>Press Release About</h5>
                                <ReactQuill tabIndex={3} theme="snow" value={prAbout} onChange={setPrAbout} >
                                    <div className="swiftrev-ql" id="b" style={{ height: "250px" }} />
                                </ReactQuill>
                                <br />
                                <h5>Header Image</h5>
                                <p style={{ fontSize: "12px" }}>Optional. This is displayed at the top of paid press releases in our Press Release Channel. Editors may also use these images in articles they write. <strong>Required file format:</strong> JPG or PNG. <strong>Recommended file dimensions:</strong> 1400x600 px (wide rectangle). <strong>File size max:</strong> 1MB.</p>
                                <Image
                                    style={{
                                        maxHeight: '150px',
                                    }}
                                    src={headerImage}
                                />
                                <Form.Group className="mb-3" controlId="headerImage">
                                    <Form.Control type="file" ref={filePickerRef} accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                                        onChange={e => onHeaderChange(e.target.value)}
                                    />
                                </Form.Group>
                                <br />
                                {uploading && <p>Uploading image...</p>}
                                <br />
                                <h5>Post Date</h5>
                                <div>
                                    <DatePicker
                                        selected={prPostDate}
                                        onChange={(date) => setPostDate(date)}
                                        showTimeSelect
                                        dateFormat="MMMM d, yyyy h:mm aa"
                                    />
                                </div>
                                <br />
                                <Form.Check
                                    type="checkbox"
                                    onChange={handleAcceptedTerms}
                                    label={<Button onClick={handleShow} style={{ padding: 0 }} variant="link">Accept {theme?.accountName} Content Policy</Button>}
                                />
                                <br />
                            </Col>
                            <Col md="4" style={{ padding: "15px" }}>
                                <h5>Company Info</h5>
                                <Image
                                    alt={currentPR?.company?.name}
                                    src={currentPR?.company?.logo}
                                    style={{
                                        width: '96px',
                                    }}
                                />
                                <br />
                                <br />
                                <p >
                                    <span style={{ fontSize: "16px" }}>{currentPR?.company?.name}</span>
                                    <br />
                                    <span style={{ fontSize: "12px" }} className="text-secondary">COMPANY</span>
                                </p>
                                <p >
                                    <span style={{ fontSize: "16px" }}><a target="__blank" href={'https://' + currentPR?.company?.domain}>{'https://' + currentPR?.company?.domain}</a></span>
                                    <br />
                                    <span style={{ fontSize: "12px" }} className="text-secondary">WEBSITE</span>
                                </p>
                                <p style={{ fontSize: "12px", fontStyle: "italic" }}>Your post will be enriched with additional company information, increasing audience understanding and awareness.</p>
                                <h5>Your Contact Info</h5>
                                <p style={{ fontSize: "12px", fontStyle: "italic" }}>This will be kept private and used for customer support, if needed</p>
                                <p >
                                    <span style={{ fontSize: "16px" }}>{currentPR?.customer?.name}</span>
                                    <br />
                                    <span style={{ fontSize: "12px" }} className="text-secondary">YOUR NAME</span>
                                </p>
                                <p >
                                    <span style={{ fontSize: "16px" }}>{currentPR?.customer?.email}</span>
                                    <br />
                                    <span style={{ fontSize: "12px" }} className="text-secondary">YOUR EMAIL</span>
                                </p>
                                <p >
                                    <span style={{ fontSize: "16px" }}>{currentPR?.customer?.phone}</span>
                                    <br />
                                    <span style={{ fontSize: "12px" }} className="text-secondary">YOUR PHONE</span>
                                </p>
                                <h5>PR Contact Info</h5>
                                <p style={{ fontSize: "12px", fontStyle: "italic" }}>This will be displayed publicly on your press release</p>
                                <p >
                                    <span style={{ fontSize: "16px" }}>{currentPR?.prContact?.name}</span>
                                    <br />
                                    <span style={{ fontSize: "12px" }} className="text-secondary">PR CONTACT NAME</span>
                                </p>
                                <p >
                                    <span style={{ fontSize: "16px" }}>{currentPR?.prContact?.email}</span>
                                    <br />
                                    <span style={{ fontSize: "12px" }} className="text-secondary">PR CONTACT EMAIL</span>
                                </p>
                                <p >
                                    <span style={{ fontSize: "16px" }}>{currentPR?.prContact?.phone}</span>
                                    <br />
                                    <span style={{ fontSize: "12px" }} className="text-secondary">PR CONTACT PHONE</span>
                                </p>
                            </Col>
                        </Row>
                        <Stack direction="horizontal" gap={3}>
                            <Button onClick={() => updateContent()}
                                disabled={working || !prTitle?.trim() || !acceptedTerms || !prPostDate}
                                size="lg" variant="swiftrev">Next</Button>
                            <LinkContainer to="../details">
                                <Button size="lg" variant="none">Back</Button>
                            </LinkContainer>
                        </Stack>
                    </Fragment>
                }
            </Container>
            <Modal show={show} onHide={handleClose} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{theme?.accountName} Content Policy</Modal.Title>
                </Modal.Header>
                <Modal.Body><div dangerouslySetInnerHTML={{ "__html": theme?.accountContentPolicy }}></div></Modal.Body>
            </Modal>
        </Fragment>
    );
}